import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
  useLayoutEffect,
} from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import AddCoinsModal from "./AddCoinsModal";
import NotificationDropdown from "./NotificationDropdown";

const navLinks = [
  { to: "/discovery", label: "Discover" },
  { to: "/matches", label: "Matches" },
  { to: "/profile", label: "Profile" },
];

const landingLinks = [
  { id: "learn", label: "Learn" },
  { id: "safety", label: "Safety" },
  { id: "support", label: "Support" },
  { id: "feedback", label: "Feedback" },
];

function Navbar() {
  const {
    user,
    logout,
    unreadByMatch,
    unreadNotificationCount,
    refreshNotificationCount,
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [showAddCoinsModal, setShowAddCoinsModal] = useState(false);
  const [activeSection, setActiveSection] = useState("learn");
  const activeSectionRef = useRef("learn");
  const [notificationDropdownOpen, setNotificationDropdownOpen] =
    useState(false);
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  const bellUnreadCount = useMemo(() => {
    const totalMessages = unreadMessageCount;
    const notificationCount = unreadNotificationCount || 0;
    return totalMessages + notificationCount;
  }, [unreadMessageCount, unreadNotificationCount]);
  const navRef = useRef(null);

  useLayoutEffect(() => {
    if (typeof window === "undefined") return;
    const updateNavHeight = () => {
      if (!navRef.current) return;
      document.documentElement.style.setProperty(
        "--auth-nav-height",
        `${navRef.current.offsetHeight}px`,
      );
    };

    updateNavHeight();
    window.addEventListener("resize", updateNavHeight);
    return () => window.removeEventListener("resize", updateNavHeight);
  }, []);

  const isLanding = useMemo(
    () => !user && location.pathname === "/",
    [user, location.pathname],
  );

  const handleLogout = useCallback(() => {
    logout();
    navigate("/login");
    setMenuOpen(false);
  }, [logout, navigate]);

  const handleNavClick = useCallback(() => {
    setMenuOpen(false);
  }, []);

  const handleSectionClick = useCallback((sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
      setActiveSection(sectionId);
      activeSectionRef.current = sectionId;
    }
    setMenuOpen(false);
  }, []);

  const handleAddCoinsSuccess = useCallback(() => {
    // Refresh user data to get updated coins
    // This will be done via the socket event or by reloading
    setMenuOpen(false);
  }, []);

  const isActive = useCallback(
    (path) => location.pathname === path,
    [location.pathname],
  );

  useEffect(() => {
    if (!isLanding) return;

    const handleScroll = () => {
      const offset = 120;
      const sections = landingLinks
        .map((link) => document.getElementById(link.id))
        .filter(Boolean);

      if (!sections.length) return;

      let current = sections[0];
      for (const section of sections) {
        if (window.scrollY + offset >= section.offsetTop) {
          current = section;
        }
      }

      if (current && current.id !== activeSectionRef.current) {
        activeSectionRef.current = current.id;
        setActiveSection(current.id);
      }
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isLanding]);

  // Calculate total unread from context (synced with ChatList in real-time)
  useEffect(() => {
    // Calculate total unread from unreadByMatch
    const total = Object.values(unreadByMatch || {}).reduce((sum, match) => {
      const special = match.unreadSpecialCount || 0;
      const normal = match.unreadNormalCount || 0;
      return sum + special + normal;
    }, 0);

    setUnreadMessageCount(total);
  }, [unreadByMatch]);

  const handleNotificationClick = useCallback(() => {
    setNotificationDropdownOpen((prev) => !prev);
    setMenuOpen(false);
  }, []);

  const handleNotificationClose = useCallback(() => {
    setNotificationDropdownOpen(false);
  }, []);

  return (
    <>
      <nav
        ref={navRef}
        data-navbar-root
        className="fixed top-0 left-0 right-0 z-50 w-full h-16 bg-white md:bg-white/80 md:backdrop-blur border-b border-gray-100 shadow-sm"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-full">
          <div className="flex items-center justify-between h-full">
            <Link
              to={user ? "/discovery" : "/"}
              className="text-2xl sm:text-3xl font-bold text-indigo-600 tracking-tight flex items-center gap-2"
              onClick={handleNavClick}
            >
              <span className="w-12 h-12 rounded-full bg-indigo-50 flex items-center justify-center text-xl">
                💕
              </span>
              <span className="hidden sm:inline">Vibely</span>
            </Link>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center gap-5">
              {user ? (
                <>
                  {navLinks.map((link) => (
                    <Link
                      key={link.to}
                      to={link.to}
                      onClick={handleNavClick}
                      className={`nav-link text-sm font-semibold transition-colors ${
                        isActive(link.to)
                          ? "nav-link-active"
                          : "text-gray-500 hover:text-gray-900"
                      }`}
                    >
                      {link.label}
                    </Link>
                  ))}
                  <Link
                    to="/chats"
                    onClick={handleNavClick}
                    className={`nav-link text-sm font-semibold transition-colors ${
                      isActive("/chats")
                        ? "nav-link-active"
                        : "text-gray-500 hover:text-gray-900"
                    }`}
                  >
                    Chats
                  </Link>
                  <div className="flex items-center gap-2 sm:gap-3">
                    {/* Notification Bell */}
                    <div className="relative">
                      <button
                        onClick={handleNotificationClick}
                        className="relative w-10 h-10 rounded-full hover:bg-gray-100 flex items-center justify-center transition text-gray-600 hover:text-gray-900"
                      >
                        <svg
                          className="w-6 h-6"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                          />
                        </svg>
                        {bellUnreadCount > 0 && (
                          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                            {bellUnreadCount > 9 ? "9+" : bellUnreadCount}
                          </span>
                        )}
                      </button>
                      <NotificationDropdown
                        isOpen={notificationDropdownOpen}
                        onClose={handleNotificationClose}
                        onMarkedRead={refreshNotificationCount}
                      />
                    </div>
                    <button
                      onClick={() => setShowAddCoinsModal(true)}
                      className="text-sm font-semibold text-indigo-600 bg-indigo-50 px-3 py-1.5 rounded-full hover:bg-indigo-100 transition whitespace-nowrap"
                    >
                      {user?.coins || 0} 💰
                    </button>
                    <button
                      onClick={handleLogout}
                      className="px-4 py-2 rounded-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-sm font-semibold shadow-md hover:shadow-lg transition whitespace-nowrap"
                    >
                      Logout
                    </button>
                  </div>
                </>
              ) : (
                <>
                  {isLanding && (
                    <div className="flex items-center gap-5 mr-2">
                      {landingLinks.map((link) => (
                        <button
                          key={link.id}
                          type="button"
                          onClick={() => handleSectionClick(link.id)}
                          className={`nav-link text-sm font-semibold transition-colors ${
                            activeSection === link.id
                              ? "nav-link-active"
                              : "text-gray-500 hover:text-gray-900"
                          }`}
                        >
                          {link.label}
                        </button>
                      ))}
                    </div>
                  )}
                  <Link
                    to="/login"
                    className="link-soft text-sm font-semibold text-gray-700 hover:text-gray-900 transition-colors"
                    onClick={handleNavClick}
                  >
                    Login
                  </Link>
                  <Link
                    to="/signup"
                    className="btn-primary px-4 py-2 rounded-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-sm font-semibold shadow-md hover:shadow-lg transition whitespace-nowrap"
                    onClick={handleNavClick}
                  >
                    Sign up
                  </Link>
                </>
              )}
            </div>

            {/* Mobile Menu Toggle */}
            <button
              className="md:hidden text-xl sm:text-2xl text-gray-700 w-10 h-10 rounded-full hover:bg-gray-100 transition"
              onClick={() => setMenuOpen((prev) => !prev)}
              aria-label="Toggle menu"
            >
              {menuOpen ? "✕" : "☰"}
            </button>
          </div>

          {/* Mobile Menu */}
          <div
            className={`md:hidden bg-white overflow-hidden transition-all duration-300 ${
              menuOpen ? "max-h-96 py-4" : "max-h-0"
            }`}
          >
            <div className="flex flex-col gap-3 px-1">
              {user ? (
                <>
                  {navLinks.map((link) => (
                    <Link
                      key={link.to}
                      to={link.to}
                      onClick={handleNavClick}
                      className={`text-sm sm:text-base font-semibold px-3 py-2 rounded-lg nav-link ${
                        isActive(link.to)
                          ? "nav-link-active"
                          : "text-gray-600 hover:text-gray-900"
                      }`}
                    >
                      {link.label}
                    </Link>
                  ))}
                  <Link
                    to="/chats"
                    onClick={handleNavClick}
                    className={`text-sm sm:text-base font-semibold px-3 py-2 rounded-lg nav-link ${
                      isActive("/chats")
                        ? "nav-link-active"
                        : "text-gray-600 hover:text-gray-900"
                    }`}
                  >
                    Chats
                    {unreadMessageCount > 0 && (
                      <span className="ml-2 bg-red-500 text-white text-xs font-bold rounded-full px-2 py-0.5">
                        {unreadMessageCount > 9 ? "9+" : unreadMessageCount}
                      </span>
                    )}
                  </Link>
                  <div className="flex flex-col gap-2 pt-2 px-2">
                    <button
                      onClick={() => {
                        setShowAddCoinsModal(true);
                        setMenuOpen(false);
                      }}
                      className="text-sm font-semibold text-indigo-600 bg-indigo-50 px-3 py-2 rounded-full hover:bg-indigo-100 transition w-full text-center"
                    >
                      {user?.coins || 0} 💰 Add Coins
                    </button>
                    <button
                      onClick={handleLogout}
                      className="px-4 py-2 rounded-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-sm font-semibold whitespace-nowrap"
                    >
                      Logout
                    </button>
                  </div>
                </>
              ) : (
                <div className="flex flex-col gap-2 px-2">
                  {isLanding && (
                    <div className="flex flex-col gap-2">
                      {landingLinks.map((link) => (
                        <button
                          key={link.id}
                          type="button"
                          onClick={() => handleSectionClick(link.id)}
                          className={`text-left text-sm font-semibold px-3 py-2 rounded-lg transition nav-link ${
                            activeSection === link.id
                              ? "nav-link-active bg-indigo-50"
                              : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                          }`}
                        >
                          {link.label}
                        </button>
                      ))}
                    </div>
                  )}
                  <Link
                    to="/login"
                    className="link-soft text-sm font-semibold text-gray-700 px-3 py-2 text-center hover:bg-gray-50 rounded-lg transition"
                    onClick={handleNavClick}
                  >
                    Login
                  </Link>
                  <Link
                    to="/signup"
                    className="btn-primary px-4 py-2 rounded-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-sm font-semibold text-center shadow-md"
                    onClick={handleNavClick}
                  >
                    Sign up
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>

      <AddCoinsModal
        isOpen={showAddCoinsModal}
        onClose={() => setShowAddCoinsModal(false)}
        onSuccess={handleAddCoinsSuccess}
      />
    </>
  );
}

export default React.memo(Navbar);
